import { List, Map } from 'immutable'
import { createSelector } from 'reselect'

import { getActiveDbtTeensEpisodes } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/utils'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { Feedback, FeedbackEntityType } from '@lyrahealth-inc/shared-app-logic/src/models/feedback/Feedback'
import { User } from '@lyrahealth-inc/shared-app-logic/src/models/user/User'

import { getUserUnregisteredMinorChildren } from '../../../data/user/userSelectors'

const getLyraTherapy = (state: Map<string, any>) => state.get('lyraTherapy', Map())

const getLyraTherapyAssignments = createSelector(getLyraTherapy, (lyraTherapy) =>
  lyraTherapy?.get('assignments', Map()),
)

export const getLyraTherapyAssignmentsData = createSelector(getLyraTherapyAssignments, (lyraTherapyAssignments) =>
  lyraTherapyAssignments?.get('data', List()),
)

export const getLyraTherapyEpisodes = createSelector(getLyraTherapy, (lyraTherapy) =>
  lyraTherapy?.get('episodes', List()),
)

export const getChildCurrentEpisodes = createSelector(
  [getLyraTherapy, (_, lyraId) => lyraId],
  (lyraTherapy, lyraId) => {
    const episodes: Episode[] | undefined = lyraTherapy?.get('childEpisodes', Map())?.get(lyraId, List())?.toJS()
    return episodes?.filter((episode) => episode.state === 'in_progress') ?? []
  },
)

const getChildEpisodesMap = createSelector(getLyraTherapy, (lyraTherapy) => lyraTherapy?.get('childEpisodes', Map()))

export const getHasReferredTeenSelector = createSelector(
  [getChildEpisodesMap, getUserUnregisteredMinorChildren],
  (childEpisodesMap, unregisteredMinorChildren) => {
    if (!unregisteredMinorChildren?.length) return false
    return unregisteredMinorChildren.some((child: User) => {
      if (!child.referrerName) return false
      const lyraId = child.lyraId ?? ''
      if (!childEpisodesMap?.has(lyraId)) return false
      const episodes = (childEpisodesMap.get(lyraId)?.toJS() ?? []) as Episode[]
      return getActiveDbtTeensEpisodes(episodes).length === 0
    })
  },
)

export const getLyraTherapyNotifications = createSelector(getLyraTherapy, (lyraTherapy) =>
  lyraTherapy?.get('notifications', Map()),
)

export const getLyraTherapyCustomNotifications = createSelector(
  getLyraTherapyNotifications,
  (lyraTherapyNotifications) => lyraTherapyNotifications?.get('customNotificationPreferences', Map()),
)

export const getLyraTherapyNotificationsPreferences = createSelector(
  getLyraTherapyNotifications,
  (lyraTherapyNotifications) => lyraTherapyNotifications?.get('preferences', Map()),
)

export const getShowSessionNotificationPrompt = createSelector(
  getLyraTherapyNotifications,
  (lyraTherapyNotifications) => lyraTherapyNotifications?.get('showSessionNotificationPrompt'),
)

export const getIntakeFormOpen = createSelector(getLyraTherapy, (lyraTherapy) => lyraTherapy?.get('intakeFormOpen'))

export const getFirstAppointmentBooked = createSelector(getLyraTherapy, (lyraTherapy) =>
  lyraTherapy?.get('firstAppointmentBooked'),
)

export const getJustShowedPushNotificationPrompt = createSelector(getLyraTherapy, (lyraTherapy) =>
  lyraTherapy?.get('justShowedPushNotificationPrompt'),
)

export const getViewedNotificationModal = createSelector(getLyraTherapy, (lyraTherapy) =>
  lyraTherapy?.get('viewedNotificationModal'),
)

export const getHasFetchedDashboard = createSelector(getLyraTherapy, (lyraTherapy) =>
  lyraTherapy?.get('dashboardFetched'),
)

export const getAiCoachFeedback = createSelector(getLyraTherapy, (lyraTherapy) =>
  lyraTherapy?.getIn(['feedback', FeedbackEntityType.AI_COACH]),
)

export const getAiCoachClientFeedback = createSelector(getAiCoachFeedback, (coachFeedback) =>
  Array.isArray(coachFeedback)
    ? coachFeedback.filter((feedback: Feedback) => feedback.user_type === 'client')
    : undefined,
)
